import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface QRCodeScannerProps {
  onScan: (data: string) => void;
  onClose: () => void;
  title?: string;
  placeholder?: string;
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
  onScan,
  onClose,
  title = "Scanner QR Code",
  placeholder = "Entrez le code manuellement",
}) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<'idle' | 'scanning' | 'result'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [scannedValue, setScannedValue] = useState<string | null>(null);
  const scannerIdRef = useRef(`qr-scanner-${Date.now()}`);

  // Cleanup function
  const cleanup = async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) { // SCANNING state
          await scannerRef.current.stop();
        }
        scannerRef.current.clear();
      } catch (err) {
        // Ignore cleanup errors
      }
      scannerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const startScanner = async () => {
    setError(null);
    setMode('scanning');
    
    // Wait for DOM to update
    await new Promise(resolve => setTimeout(resolve, 200));
    
    try {
      // Make sure previous scanner is cleaned up
      await cleanup();
      
      const element = document.getElementById(scannerIdRef.current);
      if (!element) {
        throw new Error("Container non trouvé");
      }

      scannerRef.current = new Html5Qrcode(scannerIdRef.current);
      
      await scannerRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        async (decodedText) => {
          // Stop scanner immediately
          await cleanup();
          handleQRDetected(decodedText);
        },
        () => {
          // QR Code not detected, continue scanning
        }
      );
    } catch (err: any) {
      console.error("Erreur scanner:", err);
      setError(err?.message || "Impossible d'accéder à la caméra. Vérifiez les permissions.");
      setMode('idle');
    }
  };

  const stopScanner = async () => {
    await cleanup();
    setMode('idle');
  };

  const handleQRDetected = (decodedText: string) => {
    // Extract ID from QR code (can be URL or direct ID)
    let extractedId = decodedText;
    
    if (decodedText.includes('driverId=')) {
      const match = decodedText.match(/driverId=([^&]+)/);
      if (match) extractedId = decodeURIComponent(match[1]);
    } else if (decodedText.includes('plate=')) {
      const match = decodedText.match(/plate=([^&]+)/);
      if (match) extractedId = decodeURIComponent(match[1]);
    }
    
    setScannedValue(extractedId);
    setMode('result');
  };

  const handleConfirm = () => {
    if (scannedValue) {
      onScan(scannedValue);
    }
  };

  const handleManualSubmit = () => {
    if (manualInput.trim()) {
      onScan(manualInput.trim().toUpperCase());
    }
  };

  const handleClose = async () => {
    await cleanup();
    onClose();
  };

  const handleRescan = async () => {
    setScannedValue(null);
    setError(null);
    await startScanner();
  };

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          bgcolor: 'primary.main',
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
          {title}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        
        {/* Mode Idle - Show start button and manual input */}
        {mode === 'idle' && (
          <Box sx={{ textAlign: 'center', width: '100%', maxWidth: 350 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<CameraAltIcon />}
              onClick={startScanner}
              sx={{
                mb: 3,
                py: 2,
                px: 4,
              }}
            >
              Activer la caméra
            </Button>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ou saisir manuellement
            </Typography>
            
            <TextField
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value.toUpperCase())}
              placeholder={placeholder}
              variant="outlined"
              fullWidth
              sx={{
                '& input': { textAlign: 'center', fontWeight: 700, letterSpacing: 1 },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleManualSubmit} disabled={!manualInput.trim()}>
                      <CheckCircleIcon color={manualInput.trim() ? 'success' : 'disabled'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleManualSubmit();
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {/* Mode Scanning - Show camera */}
        {mode === 'scanning' && (
          <Box sx={{ width: '100%', maxWidth: 350, textAlign: 'center' }}>
            <Box
              id={scannerIdRef.current}
              ref={containerRef}
              sx={{
                width: '100%',
                minHeight: 300,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#000',
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Pointez la caméra vers le QR code
            </Typography>
            <Button
              variant="outlined"
              onClick={stopScanner}
              sx={{ mt: 2 }}
              fullWidth
            >
              Annuler
            </Button>
          </Box>
        )}

        {/* Mode Result - Show scanned value */}
        {mode === 'result' && scannedValue && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
              QR Code détecté
            </Typography>
            <Typography
              variant="h6"
              sx={{
                bgcolor: 'grey.100',
                px: 3,
                py: 1,
                borderRadius: 2,
                fontFamily: 'monospace',
                mb: 3,
              }}
            >
              {scannedValue}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleRescan}
                startIcon={<EditIcon />}
              >
                Rescanner
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirm}
                startIcon={<CheckCircleIcon />}
              >
                Confirmer
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default QRCodeScanner;
